import { ref } from 'vue'
import { useRoute } from 'vue-router'
import { defineStore } from 'pinia'
import { useFetch } from '@/modules/core/composables/useFetch'
import { Task, TaskList } from '../types'

export const useTaskListStore = defineStore('tasking/taskListStore', () => {
  const { slug } = useRoute().params as { entity_id: string; slug: string }
  const baseUrl = `/v4/${slug}/tasking`

  //  loading

  const loadingIds = ref(new Set<number>())
  const isLoading = (id: number) => loadingIds.value.has(id)
  const setLoading = (id: number, loading: boolean) => {
    if (loading) {
      loadingIds.value.add(id)
    } else {
      loadingIds.value.delete(id)
    }
  }

  // state

  const taskList = ref<TaskList>()

  // queries

  const fetchTaskList = async (taskListId: number | string) => {
    taskList.value = await useFetch(`${baseUrl}/task-lists/${taskListId}`).get<TaskList>()
  }

  // commands

  const completeTaskList = async (taskListId: number | string) => {
    const incompleteTasks = taskList.value?.tasks.filter((task) => !isTaskComplete(task))
    incompleteTasks.forEach((task) => setLoading(task.id, true))

    await useFetch(`${baseUrl}/task-lists/${taskListId}/complete`).post()
    await fetchTaskList(taskListId)

    incompleteTasks.forEach((task) => setLoading(task.id, false))
  }

  const hideTaskList = async (taskListId: number | string) => {
    try {
      taskList.value.is_visible = false
      await useFetch(`${baseUrl}/task-lists/${taskListId}/hide`).post()
    } finally {
      await fetchTaskList(taskListId)
    }
  }

  const reopenTaskList = async (taskListId: number | string) => {
    await useFetch(`${baseUrl}/task-lists/${taskListId}/reopen`).post()
    await fetchTaskList(taskListId)
  }

  const showTaskList = async (taskListId: number | string) => {
    try {
      taskList.value.is_visible = true
      await useFetch(`${baseUrl}/task-lists/${taskListId}/show`).post()
    } finally {
      await fetchTaskList(taskListId)
    }
  }

  // Tasks

  const completeTask = async (taskId: number) => {
    try {
      setLoading(taskId, true)
      await useFetch(`${baseUrl}/tasks/${taskId}/complete`).post()
    } catch (error) {
      await fetchTaskList(taskList.value?.id)
    } finally {
      setLoading(taskId, false)
    }
  }

  const reopenTask = async (taskId: number) => {
    try {
      setLoading(taskId, true)
      await useFetch(`${baseUrl}/tasks/${taskId}/reopen`).post()
    } catch (error) {
      await fetchTaskList(taskList.value?.id)
    } finally {
      setLoading(taskId, false)
    }
  }

  // helpers

  const isTaskComplete = (task: Task) => task?.status === 'complete'

  const isTaskIncomplete = (task: Task) => task?.status === 'incomplete'

  return {
    taskList,
    fetchTaskList,

    completeTaskList,
    hideTaskList,
    reopenTaskList,
    showTaskList,
    completeTask,
    reopenTask,

    isLoading,
    setLoading,

    isTaskComplete,
    isTaskIncomplete,
  }
})
